import { types, getParent, Instance, getRelativePath, IOptionalIType } from 'mobx-state-tree'
import { IPrintedcircuitboardProperties } from 'src/api/models/Content/Printedcircuitboard/types'
import { Side } from 'src/api/models/values'
import { top, Side as SideEnum } from 'src/constants/sides'
import { generateType } from 'src/api/models/utils/generateNodeType'
import { contentEdgeProperties, gap, gapSide } from 'src/constants/typenames'
import { contentEdgeParameters } from 'src/constants/parameters'
import { UnitNumberValue } from 'src/api/models/values'
import { IContentEdge, IContentEdgeProperties, IGap, IGapSide } from './types'
import { generateLimitedUnitNumber } from '../../Values/generateLimitedUnitNumber'

type Limit = { [key in 'min' | 'max']: number }

type GetDynamicLimit = (arg: {
  staticLimit: Limit
  nodeProperties: Instance<IPrintedcircuitboardProperties>
  edgeProperties: any // Instance<ReturnType<typeof ContentEdgeProperties>>
}) => Limit

type GapSideConfig = {
  /** allowed values */
  values?: number[]
  staticLimit: Limit
  defaultValue: number
} & {
  getLimit: GetDynamicLimit
}

const gapConfiguration: { [key in SideEnum]: GapSideConfig } = {
  front: {
    defaultValue: 0,
    staticLimit: { min: 0, max: 300 },
    getLimit: ({
      staticLimit: { min, max },
      nodeProperties: { width },
      edgeProperties: { gap }
    }) => ({
      min,
      max: max - (gap as Instance<typeof Gap>).back.value - width.value
    })
  },
  top: {
    defaultValue: 0,
    staticLimit: { min: 0, max: 300 },
    getLimit: ({
      staticLimit: { min, max },
      nodeProperties: { thickness, componentSide, solderSide },
      edgeProperties: { gap }
    }) => ({
      min,
      max:
        max -
        gap.bottom.value -
        thickness.value -
        componentSide.height.value -
        solderSide.height.value
    })
  },
  right: {
    values: [7],
    defaultValue: 7,
    staticLimit: { min: 0, max: 300 },
    getLimit: ({
      staticLimit: { min, max },
      nodeProperties: { length },
      edgeProperties: { gap }
    }) => ({
      min,
      max: max - gap.left.value - length.value
    })
  },
  bottom: {
    defaultValue: 0,
    staticLimit: { min: 0, max: 300 },
    getLimit: ({
      staticLimit: { min, max },
      nodeProperties: { thickness, componentSide, solderSide },
      edgeProperties: { gap }
    }) => ({
      min,
      max:
        max - gap.top.value - thickness.value - componentSide.height.value - solderSide.height.value
    })
  },
  left: {
    values: [7],
    defaultValue: 7,
    staticLimit: { min: 0, max: 300 },
    getLimit: ({
      staticLimit: { min, max },
      nodeProperties: { length },
      edgeProperties: { gap }
    }) => ({
      min,
      max: max - gap.right.value - length.value
    })
  },
  back: {
    defaultValue: 0,
    staticLimit: { min: 0, max: 300 },
    getLimit: ({
      staticLimit: { min, max },
      nodeProperties: { width },
      edgeProperties: { gap }
    }) => ({
      min,
      max: max - gap.front.value - width.value
    })
  }
}

const generateGapSide = ({
  values,
  defaultValue,
  staticLimit,
  getLimit
}: GapSideConfig): IGapSide => {
  let checkValue: (value: number) => boolean
  let valueType
  if (values) {
    checkValue = (value: number) => values.includes(value)
    valueType = types.union(...values.map(v => types.literal(v)))
  } else {
    checkValue = (value: number) => value >= staticLimit.min && value <= staticLimit.max
    valueType = types.refinement(types.number, checkValue)
  }
  return generateType(gapSide, UnitNumberValue)
    .props({
      value: types.optional(valueType, defaultValue)
    })
    .views(self => {
      const contentEdge = getParent<Instance<IContentEdge>>(self, 3)
      return {
        get side() {
          const pathSide = getRelativePath(getParent(self), self).substr(1)
          return pathSide
        },
        get min() {
          return getLimit({
            staticLimit,
            nodeProperties: contentEdge.node.properties,
            edgeProperties: contentEdge.properties
          }).min
        },
        get max() {
          return getLimit({
            staticLimit,
            nodeProperties: contentEdge.node.properties,
            edgeProperties: contentEdge.properties
          }).max
        }
      }
    })
    .actions(self => ({
      afterAttach() {},
      update(value: typeof self.value) {
        if (checkValue(value)) {
          self.value = value
        }
      }
    }))
}

const gapSides = Object.entries(contentEdgeParameters.properties.gap).reduce(
  (prev, [side, gapSideConfig]) => ({
    ...prev,
    [side]: types.optional(generateLimitedUnitNumber(gapSideConfig), gapSideConfig)
  }),
  {} as { [key in SideEnum]: IOptionalIType<IGapSide, [undefined]>  }
)

// Object.entries(gapConfiguration).reduce(
//   (prev, [side, gapSideConfig]) => ({
//     ...prev,
//     [side]: types.optional(generateGapSide(gapSideConfig), {})
//   }),
//   {} as { [key in SideEnum]: IOptionalIType<IGapSide, [undefined]> }
// )

const Gap: IGap = generateType(gap).props(gapSides)

export const ContentEdgeProperties = (): IContentEdgeProperties =>
  generateType(contentEdgeProperties).props({
    // side: types.optional(Side, top),
    gap: types.optional(Gap, {})
  })

const ConditionalGapSide = types.model({
  value: types.optional(types.number, 0)
})
